import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Printer } from "lucide-react";
import {
  ROLE_HEADER,
  type BranchesResponse,
  type CircleListResponse,
  type ReportResponse,
  type ReportRow,
} from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, PageTitle } from "../components/ui";
import { api, errorText, getStoredRole, send } from "../lib/api";
import { useOrg } from "../lib/org";
import { useMe } from "../lib/session";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const pct = (v: number | null) => (v === null ? "لا بيانات" : `${Math.round(v * 100)}٪`);
const rowLabel = { branch: "المجمع", circle: "الحلقة", student: "الطالب" } as const;

type Scope = { type: "organization" | "branch" | "circle"; id?: string };

/** Reports (docs/05 §5): the shared metrics over a period, by branch, circle or student. Real data only. */
export function ReportsPage() {
  const org = useOrg();
  const { data: me } = useMe();
  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
  });
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
  });
  const canOrg = me?.activeRole === "org_admin";
  const [scope, setScope] = useState<Scope | null>(null);
  useEffect(() => {
    if (scope || !me) return;
    if (canOrg) setScope({ type: "organization" });
    else if (branches.data?.branches[0]) setScope({ type: "branch", id: branches.data.branches[0].id });
  }, [scope, me, canOrg, branches.data]);

  const today = iso(new Date());
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(today);
  const qs =
    org && scope
      ? new URLSearchParams({
          organizationId: org.id,
          scopeType: scope.type,
          from,
          to,
          ...(scope.id ? { scopeId: scope.id } : {}),
        }).toString()
      : null;
  const report = useQuery({
    queryKey: ["report", qs],
    queryFn: () => api<ReportResponse>(`/v1/reports?${qs}`),
    enabled: !!qs && from <= to,
  });
  const [exportError, setExportError] = useState<string | null>(null);

  async function exportExcel() {
    setExportError(null);
    try {
      const role = getStoredRole();
      const res = await fetch(`/v1/reports/export.xlsx?${qs}`, {
        credentials: "same-origin",
        headers: role ? { [ROLE_HEADER]: role } : {},
      });
      if (!res.ok) throw new Error("تعذر التصدير");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `mubin-report-${from}-${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(errorText(e));
    }
  }

  // The browser prints or saves the PDF (it shapes Arabic correctly); the server records it first.
  async function printReport() {
    setExportError(null);
    try {
      await send("POST", `/v1/reports/print?${qs}`);
      window.print();
    } catch (e) {
      setExportError(errorText(e));
    }
  }

  const scopeValue = scope ? `${scope.type}:${scope.id ?? ""}` : "";
  const r = report.data;

  return (
    <>
      <PageTitle
        title="التقارير"
        sub="مؤشرات موحدة من البيانات الفعلية فقط. «لا بيانات» تعني أنه لا شيء يُقاس في الفترة."
        actions={
          r ? (
            <div className="row-actions no-print">
              <button className="btn btn-secondary" onClick={() => void printReport()}>
                <Printer size={16} strokeWidth={1.75} /> طباعة / PDF
              </button>
              <button className="btn btn-secondary" onClick={() => void exportExcel()}>
                <Download size={16} strokeWidth={1.75} /> تصدير Excel
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="card no-print">
        <div className="form section">
          <div className="form-row">
            <Field id="rp-scope" label="النطاق">
              <select
                id="rp-scope"
                value={scopeValue}
                onChange={(e) => {
                  const [type, id] = e.target.value.split(":") as [Scope["type"], string];
                  setScope({ type, ...(id ? { id } : {}) });
                }}
              >
                {canOrg && <option value="organization:">الجمعية كلها (حسب المجمع)</option>}
                <optgroup label="مجمع (حسب الحلقة)">
                  {branches.data?.branches.map((b) => (
                    <option key={b.id} value={`branch:${b.id}`}>
                      {b.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="حلقة (حسب الطالب)">
                  {circles.data?.circles.map((c) => (
                    <option key={c.id} value={`circle:${c.id}`}>
                      {c.name} · {c.branchName}
                    </option>
                  ))}
                </optgroup>
              </select>
            </Field>
            <Field id="rp-from" label="من">
              <input
                id="rp-from"
                type="date"
                dir="ltr"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field id="rp-to" label="إلى">
              <input
                id="rp-to"
                type="date"
                dir="ltr"
                value={to}
                min={from}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
          </div>
        </div>
      </section>
      <ErrorNote error={exportError ?? (report.error ? errorText(report.error) : null)} />
      {report.isPending && qs ? (
        <Loading rows={4} />
      ) : r ? (
        <>
          <div className="print-only print-head">
            <b>{org?.name}</b> · تقرير {r.scope.name} · من{" "}
            <bdi dir="ltr" className="num">
              {r.from}
            </bdi>{" "}
            إلى{" "}
            <bdi dir="ltr" className="num">
              {r.to}
            </bdi>
            <div className="small">طُبع في {new Date().toLocaleString("ar-SA-u-nu-latn-ca-gregory")}</div>
          </div>
          {!r.definitionsApproved && (
            <p className="muted small">تعريفات المؤشرات لم تُعتمد بعد (docs/05 §5).</p>
          )}
          <div className="metric-grid">
            <Metric label="نسبة الحضور" value={pct(r.total.metrics.attendanceRate)} />
            <Metric label="إنجاز الخطة" value={pct(r.total.metrics.planCompletion)} />
            <Metric label="إنجاز المراجعة" value={pct(r.total.metrics.reviewCompletion)} />
            <Metric
              label="الحفظ الجديد"
              value={`${r.total.metrics.memorizedPages} وجه`}
              sub={`${r.total.metrics.memorizedAyahs} آية`}
            />
            <Metric label="نجاح الجلسات المباشرة" value={pct(r.total.metrics.sessionSuccessRate)} />
            <Metric
              label="يحتاجون متابعة"
              value={String(r.total.metrics.strugglingStudents)}
              sub={`من ${r.total.counts.students} طالب`}
            />
          </div>
          <section className="card">
            <div className="card-h">
              <h2>حسب {rowLabel[r.rowKind]}</h2>
            </div>
            {r.rows.length === 0 ? (
              <Empty title="لا بيانات في هذه الفترة" />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{rowLabel[r.rowKind]}</th>
                      {r.rowKind !== "student" && <th>الطلاب</th>}
                      <th>الحضور</th>
                      <th>إنجاز الخطة</th>
                      <th>المراجعة</th>
                      <th>الحفظ</th>
                      <th>الجلسات</th>
                      <th>متابعة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.rows.map((row) => (
                      <Row key={row.id} row={row} kind={r.rowKind} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card metric">
      <span className="muted small">{label}</span>
      <b className="metric-value">{value}</b>
      {sub && <span className="muted small">{sub}</span>}
    </div>
  );
}

function Row({ row, kind }: { row: ReportRow; kind: ReportResponse["rowKind"] }) {
  const m = row.metrics;
  return (
    <tr>
      <td>{row.name}</td>
      {kind !== "student" && <td className="num">{row.counts.students}</td>}
      <td className="num">{pct(m.attendanceRate)}</td>
      <td className="num">{pct(m.planCompletion)}</td>
      <td className="num">{pct(m.reviewCompletion)}</td>
      <td className="num small">
        {m.memorizedPages} وجه · {m.memorizedAyahs} آية
      </td>
      <td className="num">{pct(m.sessionSuccessRate)}</td>
      <td>{m.strugglingStudents > 0 ? <Badge tone="warning">{m.strugglingStudents}</Badge> : "—"}</td>
    </tr>
  );
}
